import { z } from 'zod';

/**
 * Ancilares aéreos pós-emissão (assento e bagagem): o que o BFF devolve ao front.
 * Contrato público — os identificadores do fornecedor (`searchKey`, `paxId`,
 * `legId`, `offerOptionId`, `offerId`) NUNCA aparecem aqui; o front só manipula
 * `seatToken`/`offerToken` opacos, válidos dentro da sessão que os emitiu.
 */

const IATA_AIRPORT = /^[A-Z]{3}$/;
const IATA_CARRIER = /^[A-Z0-9]{2}$/;

/** Valor monetário como o `FlightSeatMapView` do genui-kit: decimal em string ("153.48"). */
export const AncillaryMoneySchema = z
  .object({
    amount: z.string().regex(/^\d+\.\d{2}$/),
    currency: z.string().regex(/^[A-Z]{3}$/),
  })
  .strict();
export type AncillaryMoney = z.infer<typeof AncillaryMoneySchema>;

export const AncillaryDirectionSchema = z.enum(['outbound', 'return']);
export type AncillaryDirection = z.infer<typeof AncillaryDirectionSchema>;

/** Por que um produto (assento ou bagagem) não está disponível para o voucher. */
export const AncillaryBlockReasonSchema = z.enum([
  'unsupported',
  'concierge_voucher',
  'not_emitted',
  'departed',
  'multi_passenger',
  'policy_denied',
  /** O módulo de ancilares está desligado neste ambiente: o overview responde 200 com os
   *  dois produtos indisponíveis, sem erro e sem log de erro em lugar nenhum. */
  'disabled',
]);
export type AncillaryBlockReason = z.infer<typeof AncillaryBlockReasonSchema>;

export const AncillaryLegSchema = z
  .object({
    legRef: z.string().min(1),
    direction: AncillaryDirectionSchema,
    origin: z.string().regex(IATA_AIRPORT),
    destination: z.string().regex(IATA_AIRPORT),
    /** Relógio local do aeroporto, sem fuso (ex.: "2026-11-10T23:10:00"). */
    departureAt: z.string().min(1),
    flightNumber: z.string().min(1),
    carrierCode: z.string().regex(IATA_CARRIER).nullable(),
  })
  .strict();
export type AncillaryLeg = z.infer<typeof AncillaryLegSchema>;

export const SeatRowSchema = z.enum(['window', 'middle', 'aisle']);
export type SeatRow = z.infer<typeof SeatRowSchema>;
/** `emergency_exit` segue no contrato (o legado e os pedidos de antes), mas a tela da #3939
 *  não o oferece: o espaço a mais é o "assento conforto", que é compra. */
export const SeatPositionSchema = z.enum(['front', 'middle', 'back', 'emergency_exit']);
export type SeatPosition = z.infer<typeof SeatPositionSchema>;

const distinct = (list: readonly unknown[]) => new Set(list).size === list.length;

/**
 * A preferência de assento do pedido sem mapa, com o contrato de "aceito" da #3939: as
 * fileiras e as posições que a pessoa ACEITA — o time escolhe a melhor disponível; lista vazia
 * = qualquer uma — e o assento conforto (mais espaço), que é compra, não preferência.
 */
export const SeatPreferenceChoiceSchema = z
  .object({
    rows: z.array(SeatRowSchema).max(3).refine(distinct, 'repeated row'),
    positions: z.array(SeatPositionSchema).max(4).refine(distinct, 'repeated position'),
    comfort: z.boolean(),
  })
  .strict();

/** Os pedidos gravados antes da #3939 (uma fileira e uma posição) valem como listas de um. */
function fromSingleChoice(value: unknown): unknown {
  if (typeof value !== 'object' || value === null || 'rows' in value) return value;
  const { row, position } = value as { row?: unknown; position?: unknown };
  return row === undefined || position === undefined
    ? value
    : { rows: [row], positions: [position], comfort: false };
}

export const SeatPreferenceSchema = z.preprocess(fromSingleChoice, SeatPreferenceChoiceSchema);
export type SeatPreference = z.infer<typeof SeatPreferenceSchema>;

export const AncillaryRequestStatusSchema = z.enum([
  'processing',
  'awaiting_quote',
  'in_attendance',
  'awaiting_approval',
  'issuing',
  'issued',
  'canceled',
  'failed',
]);
export type AncillaryRequestStatus = z.infer<typeof AncillaryRequestStatusSchema>;

/**
 * Os abertos, do "mais aberto" para o menos: é a ordem em que um pedido de dois sentidos com
 * andamentos diferentes escolhe o seu status agregado (`aggregateRequestStatus`).
 */
const OPEN_AGGREGATE_ORDER: readonly AncillaryRequestStatus[] = [
  'processing',
  'in_attendance',
  'issuing',
  'awaiting_approval',
  'awaiting_quote',
];

/**
 * O status do PEDIDO a partir do status de cada item (cada sentido/perna anda sozinho no
 * legado — um voucher filho por sentido). Regra:
 *  - todos iguais → esse valor;
 *  - senão, se algum item ainda está aberto, o "mais aberto" deles
 *    (processing > in_attendance > issuing > awaiting_approval > awaiting_quote);
 *  - senão, `issued` se algum item emitiu (o que falta foi cancelado ou falhou);
 *  - senão `canceled` se algum foi cancelado, e `failed` só quando todos falharam.
 * Serve de compatibilidade (o resumo do pedido); trava e mensagem decidem pelo ITEM.
 */
export function aggregateRequestStatus(
  statuses: readonly AncillaryRequestStatus[],
): AncillaryRequestStatus {
  const [first] = statuses;
  if (first === undefined) return 'processing';
  if (statuses.every((status) => status === first)) return first;
  const open = OPEN_AGGREGATE_ORDER.find((status) => statuses.includes(status));
  if (open) return open;
  if (statuses.includes('issued')) return 'issued';
  return statuses.includes('canceled') ? 'canceled' : 'failed';
}

/** O andamento de UM item — o do(s) voucher(s) filho(s) do sentido dele. Ausente nos
 *  payloads anteriores ao campo: aí vale o `status` do pedido. */
const itemStatus = AncillaryRequestStatusSchema.optional();

export const AncillaryRequestItemSchema = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('seat'),
      direction: AncillaryDirectionSchema,
      seat: z.string().min(1),
      /** A perna do mapa em que o assento foi pedido (`outbound-1`); ausente nos pedidos
       *  gravados antes do campo, que valem para o sentido inteiro. */
      legRef: z.string().min(1).optional(),
      status: itemStatus,
    })
    .strict(),
  z
    .object({
      type: z.literal('seat_preference'),
      direction: AncillaryDirectionSchema,
      preference: SeatPreferenceSchema,
      status: itemStatus,
    })
    .strict(),
  z
    .object({
      type: z.literal('baggage'),
      direction: AncillaryDirectionSchema,
      bags: z.number().int().min(1).max(4),
      /** Peso por mala pedido fora da oferta (a mala de 32 kg); ausente = a mala da tarifa. */
      weightKg: z.number().int().positive().optional(),
      /** Total da plataforma que o cliente viu. Na v1 é referência para o atendente. */
      quoted: AncillaryMoneySchema.optional(),
      status: itemStatus,
    })
    .strict(),
]);
export type AncillaryRequestItem = z.infer<typeof AncillaryRequestItemSchema>;

/**
 * Como um voucher filho FECHADO terminou. O `status` do filho diz só `canceled` para os três
 * (é o que libera o sentido para um pedido novo); o desfecho separa a recusa do aprovador, o
 * pedido que venceu e o cancelamento — o voucher filho mostra cada um de um jeito.
 */
export const AncillaryChildOutcomeSchema = z.enum(['declined', 'expired', 'cancelled']);
export type AncillaryChildOutcome = z.infer<typeof AncillaryChildOutcomeSchema>;

/**
 * UM voucher filho do pedido — o legado cria um por sentido. É o que o voucher filho da tela
 * precisa e o pedido agregado não diz: o código e o preço DE CADA sentido, o andamento dele e
 * quando ele mudou pela última vez (a data de "Emitido em", "Recusado em").
 */
export const AncillaryRequestChildSchema = z
  .object({
    code: z.string().min(1),
    /** `null` no filho legado sem sentido: ele vale para o pedido inteiro. */
    direction: AncillaryDirectionSchema.nullable(),
    status: AncillaryRequestStatusSchema,
    outcome: AncillaryChildOutcomeSchema.optional(),
    /** Preço que o legado gravou neste filho; ausente enquanto não há cotação. */
    amount: AncillaryMoneySchema.optional(),
    updatedAt: z.iso.datetime(),
  })
  .strict();
export type AncillaryRequestChild = z.infer<typeof AncillaryRequestChildSchema>;

export const AncillaryRequestSchema = z
  .object({
    requestId: z.string().min(1),
    kind: z.enum(['seat', 'baggage']),
    /** O agregado dos itens (`aggregateRequestStatus`); cada item traz o seu. */
    status: AncillaryRequestStatusSchema,
    createdAt: z.iso.datetime(),
    items: z.array(AncillaryRequestItemSchema).min(1),
    /** Preço gravado no voucher filho pelo legado (cotação automática ou manual). */
    legacyAmount: AncillaryMoneySchema.optional(),
    childVoucherCodes: z.array(z.string().min(1)),
    /** Os filhos um a um, na ordem em que o legado os criou. Ausente nos payloads anteriores
     *  ao campo: aí só há os códigos soltos, sem saber de qual sentido é cada um. */
    children: z.array(AncillaryRequestChildSchema).optional(),
  })
  .strict();
export type AncillaryRequest = z.infer<typeof AncillaryRequestSchema>;

export const AncillaryProductAvailabilitySchema = z
  .object({ available: z.boolean(), reason: AncillaryBlockReasonSchema.optional() })
  .strict();
export type AncillaryProductAvailability = z.infer<typeof AncillaryProductAvailabilitySchema>;

export const AncillaryBaggageAvailabilitySchema = AncillaryProductAvailabilitySchema.extend({
  includedInFare: z.boolean().nullable(),
}).strict();
export type AncillaryBaggageAvailability = z.infer<typeof AncillaryBaggageAvailabilitySchema>;

/**
 * O assento do overview: a disponibilidade e a marcação que a TARIFA de cada sentido declara
 * (`SEATS_MARKING`, ver `seatMarkingIncluded`) — `true` = incluída, `false` = paga, `null` = a
 * tarifa não diz. `comfortAllowed` é a política da empresa para o assento conforto
 * (`policies.comfortSeat` do voucher): com `false`, a preferência não o oferece. Os dois ficam
 * ausentes nos overviews anteriores aos campos e no módulo desligado.
 */
const AncillarySeatAvailabilitySchema = AncillaryProductAvailabilitySchema.extend({
  includedInFare: z
    .object({ outbound: z.boolean().nullable(), return: z.boolean().nullable() })
    .strict()
    .optional(),
  comfortAllowed: z.boolean().nullable().optional(),
}).strict();

export const AncillaryOverviewSchema = z
  .object({
    voucherCode: z.string().min(1),
    seat: AncillarySeatAvailabilitySchema,
    baggage: AncillaryBaggageAvailabilitySchema,
    legs: z.array(AncillaryLegSchema),
    /** Mais recentes primeiro. */
    requests: z.array(AncillaryRequestSchema),
  })
  .strict();
export type AncillaryOverview = z.infer<typeof AncillaryOverviewSchema>;

export const AncillarySeatStateSchema = z.enum(['free', 'paid', 'occupied']);
export type AncillarySeatState = z.infer<typeof AncillarySeatStateSchema>;

export const AncillarySeatSchema = z
  .object({
    column: z.string().min(1),
    designator: z.string().min(1),
    state: AncillarySeatStateSchema,
    price: AncillaryMoneySchema.optional(),
    characteristics: z.array(z.enum(['window', 'middle', 'aisle'])),
    seatToken: z.string().min(1).optional(),
  })
  .strict()
  .superRefine((seat, ctx) => {
    if (seat.state === 'occupied' && seat.seatToken !== undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['seatToken'],
        message: 'occupied seat carries no token',
      });
    }
    if (seat.state !== 'occupied' && seat.seatToken === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['seatToken'],
        message: 'selectable seat needs a token',
      });
    }
    if (seat.state === 'paid' && seat.price === undefined) {
      ctx.addIssue({ code: 'custom', path: ['price'], message: 'paid seat needs a price' });
    }
  });
export type AncillarySeat = z.infer<typeof AncillarySeatSchema>;

/** A cabine de uma seção do mapa, em vocabulário NOSSO — `null` quando a companhia não diz
 *  qual é. */
export const AncillaryCabinSchema = z.enum(['economy', 'premium_economy', 'business', 'first']);
export type AncillaryCabin = z.infer<typeof AncillaryCabinSchema>;

/** O nome livre da cabine na fonte ("Economy", "EXECUTIVE", "Econômica") → vocabulário nosso.
 *  `premium` antes de `econom`: "Premium Economy" e "ECONOMY_PREMIUM" contêm os dois. */
const CABIN_NAMES: ReadonlyArray<[RegExp, AncillaryCabin]> = [
  [/premium/, 'premium_economy'],
  [/econom/, 'economy'],
  [/business|execut/, 'business'],
  [/first|primeira/, 'first'],
];

/** `null` quando a fonte não nomeia a cabine ou usa um nome que não reconhecemos. */
export function ancillaryCabinOf(name: string | null | undefined): AncillaryCabin | null {
  if (!name) return null;
  const normalized = name
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  return CABIN_NAMES.find(([pattern]) => pattern.test(normalized))?.[1] ?? null;
}

/**
 * Uma cabine do avião dentro do mapa, com a SUA geometria (colunas com `aisleAfter`, filas por
 * número, como o `FlightSeatMapView` do genui-kit): a executiva 1-2-1 não herda o 3-3-3 da
 * econômica. O legado entrega a perna numa lista plana, com um `layout` só; quem separa as
 * cabines e deduz os corredores é o modal-session.
 */
export const AncillarySeatMapSectionSchema = z
  .object({
    cabin: AncillaryCabinSchema.nullable(),
    columns: z.array(z.object({ letter: z.string().min(1), aisleAfter: z.boolean() }).strict()),
    rows: z.array(
      z
        .object({ number: z.number().int().positive(), seats: z.array(AncillarySeatSchema) })
        .strict(),
    ),
  })
  .strict();
export type AncillarySeatMapSection = z.infer<typeof AncillarySeatMapSectionSchema>;

/**
 * Um voo do mapa: as cabines em que a pessoa pode escolher, da frente para trás, cada uma com
 * `designator`, `characteristics` e o `seatToken` de seleção por poltrona.
 */
export const AncillarySeatMapLegSchema = z
  .object({
    legRef: z.string().min(1),
    direction: AncillaryDirectionSchema,
    origin: z.string().regex(IATA_AIRPORT),
    destination: z.string().regex(IATA_AIRPORT),
    flightNumber: z.string().min(1).nullable(),
    sections: z.array(AncillarySeatMapSectionSchema).min(1),
    summary: z
      .object({
        totalSeats: z.number().int().nonnegative(),
        availableSeats: z.number().int().nonnegative(),
        minPaid: AncillaryMoneySchema.optional(),
      })
      .strict(),
  })
  .strict();
export type AncillarySeatMapLeg = z.infer<typeof AncillarySeatMapLegSchema>;

export const AncillaryBaggageOfferSchema = z
  .object({
    offerToken: z.string().min(1),
    /** 1ª a 4ª mala; a lista sai ordenada por posição. */
    position: z.number().int().min(1).max(4),
    weight: z.object({ amount: z.number().positive(), unit: z.literal('kg') }).strict(),
    price: AncillaryMoneySchema,
  })
  .strict();
export type AncillaryBaggageOffer = z.infer<typeof AncillaryBaggageOfferSchema>;

export const AncillaryBaggageDirectionSchema = z
  .object({
    direction: AncillaryDirectionSchema,
    coverage: z.array(
      z
        .object({
          origin: z.string().regex(IATA_AIRPORT),
          destination: z.string().regex(IATA_AIRPORT),
        })
        .strict(),
    ),
    offers: z.array(AncillaryBaggageOfferSchema),
    refundable: z.literal(false),
  })
  .strict();
export type AncillaryBaggageDirection = z.infer<typeof AncillaryBaggageDirectionSchema>;

export const AncillarySessionKindSchema = z.enum([
  'seat_map',
  'baggage_quote',
  'seat_preference',
  'baggage_request',
]);
export type AncillarySessionKind = z.infer<typeof AncillarySessionKindSchema>;

export const AncillarySessionStatusSchema = z.enum([
  'loading',
  'ready',
  'unavailable',
  'failed',
  'expired',
  'submitting',
  'submitted',
  'handoff',
]);
export type AncillarySessionStatus = z.infer<typeof AncillarySessionStatusSchema>;

export const AncillaryUnavailableReasonSchema = z.enum([
  'pnr_not_ready',
  'no_map_for_fare',
  'provider_error',
  'no_offers',
  'multi_passenger',
]);
export type AncillaryUnavailableReason = z.infer<typeof AncillaryUnavailableReasonSchema>;

export const AncillarySessionSchema = z
  .object({
    sessionId: z.string().min(1),
    kind: AncillarySessionKindSchema,
    status: AncillarySessionStatusSchema,
    unavailableReason: AncillaryUnavailableReasonSchema.optional(),
    expiresAt: z.iso.datetime().optional(),
    seatMap: z
      .object({ legs: z.array(AncillarySeatMapLegSchema) })
      .strict()
      .optional(),
    baggage: z
      .object({ directions: z.array(AncillaryBaggageDirectionSchema) })
      .strict()
      .optional(),
    request: AncillaryRequestSchema.optional(),
  })
  .strict();
export type AncillarySession = z.infer<typeof AncillarySessionSchema>;
